import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfoPage from ".";
import { persistAuthResult } from "../../utils/authHelpers";
import { getReturnTo } from "../../utils/returnTo";
import { PERSONA_HOME } from "../../contexts/PersonaContext";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('reason') === 'session_expired') {
      setSessionExpired(true);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        setSubmitStatus('error');
        return;
      }

      persistAuthResult({ email, token: json.token, user: json.user });
      const persona = json.user?.activePersona || json.user?.userType || json.user?.roles?.[0];
      // ?next=/learn… comes from Postaway Academy (claim-access emails and the
      // hub's own sign-in prompt). Only same-origin hub paths are honoured.
      const next = new URLSearchParams(location.search).get('next') || '';
      const safeNext = /^\/learn(\/[A-Za-z0-9\-_/?=&%.]*)?$/.test(next) ? next : '';
      navigate(getReturnTo() || safeNext || PERSONA_HOME[persona] || '/dashboard');
    } catch (_) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const belowContent = (
    <>
      <div className="login-left">
        <img src="/contact.png" alt="Postaway member" className="login-image" />
        <p className="login-welcome">Welcome back</p>
      </div>

      <div className="login-right">
        <form onSubmit={handleSubmit} className="login-form">
          <h3 className="login-title">Member Login</h3>

          {sessionExpired && (
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffc107',
              color: '#856404',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '14px',
            }}>
              Your session has expired. Please log in again.
            </div>
          )}

          <div className="login-input-group">
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="login-input" />
            <label className="login-label">Email Address</label>
          </div>

          <div className="login-input-group">
            <div className="login-password-wrapper">
              <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required className="login-input login-input-password" />
              <button type="button" onClick={() => setShowPassword(s => !s)} aria-label="Toggle password visibility" aria-pressed={showPassword} className="login-password-toggle">
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
            <label className="login-label">
              Password <small><a href="/forgot-password" style={{ textDecoration: 'none', color: 'inherit' }}>Forgot Password</a></small>
            </label>
          </div>

          <button type="submit" disabled={isSubmitting} className="login-submit">{isSubmitting ? 'Logging in...' : 'Login'}</button>

          <div className="login-signup-link-below">
            Not a member yet? <a href="/profile-selector">Choose a profile</a>
          </div>

          {submitStatus === 'error' && (
            <p className="login-error">Invalid email or password.</p>
          )}
        </form>
      </div>
    </>
  );

  return <InfoPage title="Member Login" divider belowContent={belowContent} disableStamps />;
};

export default Login;
